<template>
  <div class="orders-page">
    <!------------------
            HEADER
    -------------------->
    <div class="bg-white p-3 pr-4 pl-4 page-header" ref="pageHeader" style="padding-bottom: 0">
      <div class="row d-flex align-items-center">
        <div class="col">
          <div>
            <h4>
              <b-icon class="me-2" icon="file-earmark"></b-icon>
              Aufträge
            </h4>
          </div>
        </div>
        <div class="col-auto">
          <div class="form-inline">
            <!---------------------------------
                HEADER ACTION BUTTONS
            ---------------------------------->
            <b-button
              variant="light"
              class="mr-2"
              @click="$router.push('/orders/create')"
              v-if="$root.$user.isPlaner || $root.$user.isAdmin"
            >
              <b-icon icon="plus" variant="dark" class="mr-1"></b-icon>
              Auftrag
            </b-button>

            <!-- SEARCH FIELD  -->
            <b-input-group class="ml-2 header-search-field">
              <b-form-input
                type="search"
                size="sm"
                v-model.lazy="searchText"
                :class="{ 'bg-primary-light-3': searchText !== '' }"
              ></b-form-input>
              <template #prepend>
                <b-input-group-text>
                  <b-icon variant="dark" icon="search" />
                </b-input-group-text>
              </template>
            </b-input-group>
            <!-- FILTER BUTTON  :disabled="!isDeleteButtonEnabled"-->
            <b-button
              size="sm"
              :variant="filterToggle == true ? 'primary' : 'light'"
              class="ml-2"
              :pressed="filterToggle"
              @click="filterRecords"
              :disabled="selectedRows.length == 0 && !filterToggle"
            >
              <b-icon icon="funnel"></b-icon>
            </b-button>
            <!-- COPY TO EXCEL  -->
            <b-button
              size="sm"
              variant="light"
              class="ml-2"
              @click="copyToExcel"
              title="In Zwischenablage für Excel kopieren"
            >
              <!-- Custom copy-to-spreadsheet icon: back sheet (copy) + front sheet with table grid -->
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="1em"
                height="1em"
                viewBox="0 0 16 16"
                fill="currentColor"
                aria-hidden="true"
                style="vertical-align: -0.125em"
              >
                <path
                  d="M6 0a1.5 1.5 0 0 0-1.5 1.5V3H6V1.5a.5.5 0 0 1 .5-.5h4v2A1.5 1.5 0 0 0 12 4.5h2v6a.5.5 0 0 1-.5.5H12v1h1.5A1.5 1.5 0 0 0 15 10.5V3.914a1.5 1.5 0 0 0-.44-1.06L12.147.439A1.5 1.5 0 0 0 11.086 0H6zm5.5 1.207L13.293 3H12a.5.5 0 0 1-.5-.5V1.207z"
                />
                <path
                  d="M1.5 5A1.5 1.5 0 0 0 0 6.5v8A1.5 1.5 0 0 0 1.5 16h7A1.5 1.5 0 0 0 10 14.5v-8A1.5 1.5 0 0 0 8.5 5h-7zM1 6.5a.5.5 0 0 1 .5-.5H4v2.25H1V6.5zm4-.5h3.5a.5.5 0 0 1 .5.5v1.75H5V6zM1 9.25h3v2.5H1v-2.5zm4 0h4v2.5H5v-2.5zM1 12.5h3V15H1.5a.5.5 0 0 1-.5-.5v-2zm4 0h4v2a.5.5 0 0 1-.5.5H5v-2.5z"
                />
              </svg>
            </b-button>
            <!-- HELP ICON  -->
            <b-button size="sm" variant="light" class="ml-2" href="https://www.bauvisor.de/auftraege/" target="_blank">
              <b-icon icon="question-circle" variant="dark" class=""></b-icon>
            </b-button>
          </div>
        </div>
      </div>
    </div>
    <div class="main-canvas">
      <div class="shadow-muted-super" ref="budgetSummarySection">
        <table class="w-100 bg-white">
          <tr class="border">
            <td class="px-2 py-1" colspan="8" v-b-toggle.collapse-orders>
              <small>Übersicht - Aktuelle Aufträge</small>
            </td>
            <td class="text-right pr-2">
              <b-button v-b-toggle.collapse-orders variant="white" size="sm">
                <span class="when-opened">
                  <b-icon icon="chevron-up" class="action-button" />
                </span>
                <span class="when-closed"><b-icon icon="chevron-down" class="action-button" /></span>
              </b-button>
            </td>
          </tr>
        </table>

        <b-collapse id="collapse-orders" :visible="isTabOpen" @shown="onWindowResize" @hidden="onWindowResize">
          <b-overlay :show="isHeaderLoading" rounded="sm" spinner-variant="primary" opacity="0.6">
            <table class="w-100 bg-white">
              <tr class="border border-top-0 border-bottom-0">
                <td class="p-2 font-weight-bold small border-bottom">VE-Budget</td>
                <td class="font-weight-bold border-bottom text-right small">
                  <div class="bg-light m-0 py-1 px-2">
                    {{ $utils.formatCurrency(this.budgetVEBudget, SettingsStore) }}
                  </div>
                </td>
                <td class="border-bottom">&nbsp;</td>
              </tr>
              <tr>
                <td class="p-2 pl-4 font-weight-bold border-left small">Entfallende Leistungen</td>
                <td class="font-weight-bold border-bottom text-right small">
                  <div class="bg-light m-0 py-1 px-2">
                    {{ $utils.formatCurrency(this.budgetVESkipped, SettingsStore) }}
                  </div>
                </td>
                <td class="border-right border-bottom text-right pr-2 small">
                  <small>{{ $utils.numberWithMaxFrictionsDE((budgetVESkipped / budgetVEBudget) * 100, 1) }}%</small>
                </td>
                <td colspan="6" class="border-right">&nbsp;</td>
              </tr>
              <tr>
                <td class="p-2 pl-4 font-weight-bold border-left border-top border-bottom small">Offenes Budget</td>
                <td class="font-weight-bold border-bottom text-right small">
                  <div class="bg-light m-0 py-1 px-2">
                    {{
                      $utils.formatCurrency(
                        this.budgetVEBudgetOpen + this.budgetVEBudgetAusgeschrieben + this.budgetVEBudgetAusgewertet,
                        SettingsStore
                      )
                    }}
                  </div>
                </td>
                <td class="border-right border-bottom text-right pr-2 small">
                  <small>
                    {{
                      $utils.numberWithMaxFrictionsDE(
                        ((this.budgetVEBudgetOpen +
                          this.budgetVEBudgetAusgeschrieben +
                          this.budgetVEBudgetAusgewertet) /
                          budgetVEBudget) *
                          100,
                        1
                      )
                    }}%
                  </small>
                </td>
                <td colspan="6" class="border-bottom border-right">&nbsp;</td>
              </tr>

              <tr>
                <td class="p-0 pt-2 pb-1 pl-4 font-weight-bold border-left small">Beauftragtes Budget</td>
                <td class="font-weight-bold text-right small">
                  <div class="bg-light m-0 py-1 px-2">
                    {{
                      $utils.formatCurrency(this.budgetVEBudgetInArbeit + this.budgetVEBudgetInvoiced, SettingsStore)
                    }}
                  </div>
                </td>
                <td class="border-right text-right pr-2 small">
                  <small>
                    {{
                      $utils.numberWithMaxFrictionsDE(
                        ((this.budgetVEBudgetInArbeit + this.budgetVEBudgetInvoiced) / budgetVEBudget) * 100,
                        1
                      )
                    }}%
                  </small>
                </td>
                <td class="p-0 pt-2 pb-1 pl-4 font-weight-bold small">Gesamtaufträge</td>
                <td class="font-weight-bold text-right small">
                  <div class="bg-light m-0 py-1 px-2">
                    {{ $utils.formatCurrency(this.budgetOrderAmountSaldo, SettingsStore) }}
                  </div>
                </td>
                <td class="border-right text-right pr-2 small">
                  <small>
                    {{
                      $utils.numberWithMaxFrictionsDE(
                        (this.budgetOrderAmountSaldo / (this.budgetVEBudgetInArbeit + this.budgetVEBudgetInvoiced)) *
                          100,
                        1
                      )
                    }}%
                  </small>
                </td>
                <td class="p-0 pt-2 pb-1 pl-4 font-weight-bold small">Nachtragsbudget</td>
                <td class="font-weight-bold text-right small">
                  <div class="bg-light m-0 py-1 px-2">
                    {{
                      $utils.formatCurrency(
                        this.budgetVEBudgetInArbeit + this.budgetVEBudgetInvoiced - this.budgetOrderAmountSaldo,
                        SettingsStore
                      )
                    }}
                  </div>
                </td>
                <td class="border-right text-right pr-2 small">
                  <small>
                    {{
                      $utils.numberWithMaxFrictionsDE(
                        ((this.budgetVEBudgetInArbeit + this.budgetVEBudgetInvoiced - this.budgetOrderAmountSaldo) /
                          (this.budgetVEBudgetInArbeit + this.budgetVEBudgetInvoiced)) *
                          100,
                        1
                      )
                    }}%
                  </small>
                </td>
              </tr>
              <tr>
                <td class="p-0 pl-5 border-left small">&nbsp;</td>
                <td class="p-0 text-right small">
                  <div class="m-0 py-1 px-2">&nbsp;</div>
                </td>
                <td class="p-0 border-right text-right pr-2 small">&nbsp;</td>
                <td class="p-0 pl-5 small">Hauptaufträge</td>
                <td class="p-0 text-right small">
                  <div class="m-0 py-1 px-2">
                    {{ $utils.formatCurrency(this.budgetOrderMainOrders, SettingsStore) }}
                  </div>
                </td>
                <td class="p-0 border-right text-right pr-2 small">
                  <small>
                    {{
                      $utils.numberWithMaxFrictionsDE(
                        (this.budgetOrderMainOrders / this.budgetOrderAmountSaldo) * 100,
                        1
                      )
                    }}%
                  </small>
                </td>
                <td class="p-0 pl-5 pb-2 small">&nbsp;</td>
                <td class="p-0 text-right small">&nbsp;</td>
                <td class="p-0 border-right text-right pr-2 small">&nbsp;</td>
              </tr>
              <tr>
                <td class="p-0 pl-5 border-left small">&nbsp;</td>
                <td class="p-0 text-right small">
                  <div class="m-0 py-1 px-2">&nbsp;</div>
                </td>
                <td class="p-0 border-right text-right pr-2 small">&nbsp;</td>
                <td class="p-0 pl-5 small">Nachträge</td>
                <td class="p-0 text-right small">
                  <div class="m-0 py-1 px-2">
                    {{ $utils.formatCurrency(this.budgetOrderOrderedAddendums, SettingsStore) }}
                  </div>
                </td>
                <td class="p-0 border-right text-right pr-2 small">
                  <small>
                    {{
                      $utils.numberWithMaxFrictionsDE(
                        (this.budgetOrderOrderedAddendums / this.budgetOrderAmountSaldo) * 100,
                        1
                      )
                    }}%
                  </small>
                </td>
                <td class="p-0 pl-5 pb-2 small">&nbsp;</td>
                <td class="p-0 text-right small">&nbsp;</td>
                <td class="p-0 border-right text-right pr-2 small">&nbsp;</td>
              </tr>

              <tr>
                <td class="p-0 pl-5 border-left small">in Arbeit befindlich</td>
                <td class="p-0 text-right small">
                  <div class="m-0 py-1 px-2">
                    {{ $utils.formatCurrency(this.budgetVEBudgetInArbeit, SettingsStore) }}
                  </div>
                </td>
                <td class="p-0 border-right text-right pr-2 small">
                  <small>
                    {{
                      $utils.numberWithMaxFrictionsDE(
                        (budgetVEBudgetInArbeit / (this.budgetVEBudgetInArbeit + this.budgetVEBudgetInvoiced)) * 100,
                        1
                      )
                    }}%
                  </small>
                </td>
                <td class="p-0 pl-5 small">in Arbeit befindlich</td>
                <td class="p-0 text-right small">
                  <div class="m-0 py-1 px-2">
                    {{ $utils.formatCurrency(this.budgetOrderAmountInArbeit, SettingsStore) }}
                  </div>
                </td>
                <td class="p-0 border-right text-right pr-2 small">
                  <small>
                    {{
                      $utils.numberWithMaxFrictionsDE((budgetOrderAmountInArbeit / budgetOrderAmountSaldo) * 100, 1)
                    }}%
                  </small>
                </td>
                <td class="p-0 pl-5 small">aktuell</td>
                <td class="p-0 text-right small">
                  <div class="m-0 py-1 px-2">
                    {{
                      $utils.formatCurrency(this.budgetVEBudgetInArbeit - this.budgetOrderAmountInArbeit, SettingsStore)
                    }}
                  </div>
                </td>
                <td class="p-0 border-right text-right pr-2 small">
                  <small>
                    {{
                      $utils.numberWithMaxFrictionsDE(
                        ((budgetVEBudgetInArbeit - budgetOrderAmountInArbeit) /
                          (this.budgetVEBudgetInArbeit + this.budgetVEBudgetInvoiced - this.budgetOrderAmountSaldo)) *
                          100,
                        1
                      )
                    }}%
                  </small>
                </td>
              </tr>
              <tr>
                <td class="p-0 pl-5 border-left small">&nbsp;</td>
                <td class="p-0 text-right small">
                  <div class="m-0 py-1 px-2">&nbsp;</div>
                </td>
                <td class="p-0 border-right text-right pr-2 small">&nbsp;</td>
                <td class="p-0 pl-5 small">&nbsp;</td>
                <td class="p-0 text-right small">&nbsp;</td>
                <td class="p-0 border-right text-right pr-2 small">&nbsp;</td>
                <td class="py-0 pb-2 small" style="padding-left: 64px">Nachträge in Prüfung</td>
                <td class="p-0 text-right pr-2 pb-2 small">
                  {{ $utils.formatCurrency(this.budgetOrderCheckingAddendums, SettingsStore) }}
                </td>
                <td class="p-0 border-right text-right pr-2 pb-2 small"></td>
              </tr>
              <tr>
                <td class="p-0 pl-5 border-left small">schlussgerechnet</td>
                <td class="text-right small">
                  <div class="m-0 py-0 px-2">
                    {{ $utils.formatCurrency(this.budgetVEBudgetInvoiced, SettingsStore) }}
                  </div>
                </td>
                <td class="border-right text-right pr-2 small">
                  <small>
                    {{
                      $utils.numberWithMaxFrictionsDE(
                        (budgetVEBudgetInvoiced / (this.budgetVEBudgetInArbeit + this.budgetVEBudgetInvoiced)) * 100,
                        1
                      )
                    }}%
                  </small>
                </td>
                <td class="p-0 pl-5 small">schlussgerechnet</td>
                <td class="text-right small">
                  <div class="m-0 py-0 px-2">
                    {{ $utils.formatCurrency(this.budgetOrderAmountInvoiced, SettingsStore) }}
                  </div>
                </td>
                <td class="border-right text-right pr-2 small">
                  <small>
                    {{
                      $utils.numberWithMaxFrictionsDE((budgetOrderAmountInvoiced / budgetOrderAmountSaldo) * 100, 1)
                    }}%
                  </small>
                </td>
                <td class="p-0 pl-5 small">nicht beansprucht</td>
                <td class="text-right small">
                  <div class="m-0 py-0 px-2">
                    {{
                      $utils.formatCurrency(this.budgetVEBudgetInvoiced - this.budgetOrderAmountInvoiced, SettingsStore)
                    }}
                  </div>
                </td>
                <td class="border-right text-right pr-2 small">
                  <small>
                    {{
                      $utils.numberWithMaxFrictionsDE(
                        ((this.budgetVEBudgetInvoiced - this.budgetOrderAmountInvoiced) /
                          (this.budgetVEBudgetInArbeit + this.budgetVEBudgetInvoiced - this.budgetOrderAmountSaldo)) *
                          100,
                        1
                      )
                    }}%
                  </small>
                </td>
              </tr>
              <tr>
                <td class="p-0 pl-5 pb-2 border-left small">&nbsp;</td>
                <td class="p-0 pb-2 text-right small">
                  <div class="m-0 py-1 px-2">&nbsp;</div>
                </td>
                <td class="p-0 pb-2 border-right text-right pr-2 small">&nbsp;</td>
                <td class="p-0 pb-2 pl-5 small">&nbsp;</td>
                <td class="p-0 pb-2 text-right small">&nbsp;</td>
                <td class="p-0 pb-2 border-right text-right pr-2 small">&nbsp;</td>
                <td class="py-0 pb-2 small" style="padding-left: 64px">Nachträge in Prüfung</td>
                <td class="p-0 pb-2 text-right pr-2 small">
                  {{ $utils.formatCurrency(this.budgetOrderCheckingAddendumsFinal, SettingsStore) }}
                </td>
                <td class="p-0 pb-2 border-right text-right pr-2 small"></td>
              </tr>
            </table>
          </b-overlay>
        </b-collapse>
      </div>
      <div class="table-wrapper table-fill shadow-muted-super mt-3" :style="{ height: tableHeight }" ref="tableWrapper">
        <b-overlay :show="isLoading" rounded="sm" spinner-variant="primary" opacity="0.6">
          <b-table
            ref="theTable"
            v-resizable-columns="{ fields: visibleFields, storageKey: 'orders', flexLast: false, minWidth: 0 }"
            :items="items"
            :fields="visibleFields"
            class="sum-header mb-0"
            head-variant="light"
            no-border-collapse
            small
            :sticky-header="tableHeight"
            :filter="searchText"
            @filtered="onFilterChange"
            show-empty
          >
            <template #head(checked)>
              <b-form-checkbox
                class="ml-2"
                :disabled="items.length == 0"
                v-model="checkedAllTableEntries"
                @change="onCheckAllTableEntries"
              ></b-form-checkbox>
            </template>
            <template #head(Ordinal)="data">
              <div :title="data.label">{{ data.label }}</div>
            </template>
            <template #head(DisplayName)="data">
              <div :title="data.label">{{ data.label }}</div>
            </template>
            <template #head(BudgetSaldo)="data">
              <div style="padding-right: 2px">
                {{ data.label }}
              </div>
              <div
                class="font-weight-bold text-right text-grey text-nowrap"
                :class="{ 'text-primary': searchText != '' || filterToggle }"
              >
                <b-icon icon="funnel-fill" v-if="searchText != '' || filterToggle" />
                {{ $utils.formatCurrency(budgetSaldoTotal, SettingsStore) }}
              </div>
            </template>
            <template #head(Amount)="data">
              <div style="padding-right: 2px">
                {{ data.label }}
              </div>
              <div
                class="font-weight-bold text-right text-grey text-nowrap"
                :class="{ 'text-primary': searchText != '' || filterToggle }"
              >
                <b-icon icon="funnel-fill" v-if="searchText != '' || filterToggle" />
                {{ $utils.formatCurrency(amountTotal, SettingsStore) }}
              </div>
            </template>
            <template #head(AmountAddendumOrdered)="data">
              <div style="padding-right: 2px">
                {{ data.label }}
              </div>
              <div
                class="font-weight-bold text-right text-grey text-nowrap"
                :class="{ 'text-primary': searchText != '' || filterToggle }"
              >
                <b-icon icon="funnel-fill" v-if="searchText != '' || filterToggle" />
                {{ $utils.formatCurrency(nachtragSummeTotal, SettingsStore) }}
              </div>
            </template>
            <template #head(NachtragsBudget)="data">
              <div style="padding-right: 2px">
                {{ data.label }}
              </div>
              <div
                class="font-weight-bold text-right text-grey text-nowrap"
                :class="{ 'text-primary': searchText != '' || filterToggle }"
              >
                <b-icon icon="funnel-fill" v-if="searchText != '' || filterToggle" />
                {{ $utils.formatCurrency(nachtragsBudgetTotal, SettingsStore) }}
              </div>
            </template>
            <template #head(AmountSaldo)="data">
              <div style="padding-right: 2px">
                {{ data.label }}
              </div>
              <div
                class="font-weight-bold text-right text-grey text-nowrap"
                :class="{ 'text-primary': searchText != '' || filterToggle }"
              >
                <b-icon icon="funnel-fill" v-if="searchText != '' || filterToggle" />
                {{ $utils.formatCurrency(totalAmountTotal, SettingsStore) }}
              </div>
            </template>
            <template #head(Invoiced)="data">
              <div style="padding-right: 2px">
                {{ data.label }}
              </div>
              <div
                class="font-weight-bold text-right text-grey text-nowrap"
                :class="{ 'text-primary': searchText != '' || filterToggle }"
              >
                <b-icon icon="funnel-fill" v-if="searchText != '' || filterToggle" />
                {{ $utils.formatCurrency(invoicedTotal, SettingsStore) }}
              </div>
            </template>
            <template #head(ObligoAmount)="data">
              <div style="padding-right: 2px">
                {{ data.label }}
              </div>
              <div
                class="font-weight-bold text-right text-grey text-nowrap"
                :class="{ 'text-primary': searchText != '' || filterToggle }"
              >
                <b-icon icon="funnel-fill" v-if="searchText != '' || filterToggle" />
                {{ $utils.formatCurrency(obligoTotal, SettingsStore) }}
              </div>
            </template>
            <template #head(AmountAddendumChecking)="data">
              <div style="padding-right: 2px">
                {{ data.label }}
              </div>
              <div
                class="font-weight-bold text-right text-grey text-nowrap"
                :class="{ 'text-primary': searchText != '' || filterToggle }"
              >
                <b-icon icon="funnel-fill" v-if="searchText != '' || filterToggle" />
                {{ $utils.formatCurrency(checkingTotal, SettingsStore) }}
              </div>
            </template>
            <template #head(actions)="data">
              {{ data.label }}

              <span @click="$bvModal.show('orders')" role="button">
                <b-icon icon="three-dots-vertical" class="float-right no-outline" style="margin-top: 4px" />
              </span>
            </template>
            <template #head(SupplierText)="data">
              <div class="supplier-text" :title="data.label">{{ data.label }}</div>
            </template>
            <template #head(KGR)="data">
              <div :title="data.label">{{ data.label }}</div>
            </template>
            <template #head(Responsible)="data">
              <div :title="data.label">{{ data.label }}</div>
            </template>
            <template #head(FA1)="data">
              <div :title="data.label">{{ data.label }}</div>
            </template>
            <template #head(FA2)="data">
              <div :title="data.label">{{ data.label }}</div>
            </template>
            <template #head(ProcessStatus)="data">
              <div :title="data.label">{{ data.label }}</div>
            </template>
            <template #head(OrderStatus)="data">
              <div :title="data.label">{{ data.label }}</div>
            </template>
            <template #head(InvoicedPercent)="data">
              <div :title="data.label">{{ data.label }}</div>
            </template>
            <template #head(HasFinalInvoice)="data">
              <div :title="data.label">{{ data.label }}</div>
            </template>
            <template #head(AbnahmeDatum)="data">
              <div :title="data.label">{{ data.label }}</div>
            </template>
            <template #cell(actions)="data">
              <b-icon
                class="action-button text-grey"
                style="width: 18px; height: 18px"
                icon="arrow-right"
                title="Auftrag öffnen"
                @click.stop="navigateTo(data.item)"
              />
              <b-icon
                class="action-button text-grey ml-2"
                style="width: 18px; height: 18px"
                icon="card-list"
                title="Nachträge"
                @click.stop="navigateTo(data.item, true)"
              />

              <b-icon
                icon="trash"
                variant="grey"
                class="action-button ml-2"
                style="width: 16px; height: 16px"
                title="Auftrag löschen"
                @click.stop="deleteRecord(data.item)"
                v-if="$root.$user.isPlaner || $root.$user.isAdmin"
              />
            </template>
            <template #cell(checked)="data">
              <b-form-checkbox
                class="ml-2"
                @change="onCheckedItemChange(data.item)"
                v-model="data.item.checked"
                :disabled="data.item.editMode"
              ></b-form-checkbox>
            </template>
            <template #cell(Ordinal)="data">
              <div @click.stop="navigateTo(data.item, true)">{{ data.item.Ordinal }}</div>
            </template>
            <template #cell(DisplayName)="data">
              <div @click.stop="navigateTo(data.item, true)">
                {{ data.item.DisplayName }}
              </div>
            </template>
            <template #cell(BudgetSaldo)="data">
              <div @click.stop="navigateToVEBudgeting(data.item)">
                {{ $utils.formatCurrency(data.item.BudgetSaldo, SettingsStore) }}
              </div>
            </template>
            <template #cell(ProcessStatus)="data">
              <div @click.stop="navigateTo(data.item, true)">
                {{ data.item.StatusText == 'offen' ? data.item.ProcessStatus : data.item.StatusText }}
              </div>
            </template>
            <template #cell(KGR)="data">
              <div @click.stop="navigateTo(data.item, true)">{{ data.item.KGR || '&nbsp;' }}</div>
            </template>
            <template #cell(Responsible)="data">
              <div @click.stop="navigateTo(data.item, true)">{{ data.item.Responsible || '&nbsp;' }}</div>
            </template>
            <template #cell(FA1)="data">
              <div @click.stop="navigateTo(data.item, true)">{{ data.item.FA1 || '&nbsp;' }}</div>
            </template>
            <template #cell(FA2)="data">
              <div @click.stop="navigateTo(data.item, true)">{{ data.item.FA2 || '&nbsp;' }}</div>
            </template>
            <template #cell(SupplierText)="data">
              <div class="supplier-text" :title="data.item.SupplierText" @click.stop="navigateTo(data.item, true)">
                {{ data.item.SupplierText }}
              </div>
            </template>
            <template #cell(OrderStatus)="data">
              <div @click.stop="navigateTo(data.item, true)">{{ data.value }}</div>
            </template>

            <template #cell(Amount)="data">
              <div @click.stop="navigateTo(data.item)">
                {{ $utils.formatCurrency(data.item.Amount, SettingsStore) }}
              </div>
            </template>
            <template #cell(AmountAddendumOrdered)="data">
              <div @click.stop="navigateTo(data.item, true)">
                {{ $utils.formatCurrency(data.item.AmountAddendumOrdered, SettingsStore) }}
              </div>
            </template>
            <template #cell(TotalAmount)="data">
              <div @click.stop="navigateTo(data.item, true)">
                {{
                  $utils.formatCurrency(
                    parseFloat(data.item.Amount) + parseFloat(data.item.AmountAddendumOrdered),
                    SettingsStore
                  )
                }}
              </div>
            </template>
            <template #cell(Invoiced)="data">
              <div @click.stop="navigateToInvoices(data.item)">
                {{ $utils.formatCurrency(data.item.Invoiced, SettingsStore) }}
              </div>
            </template>
            <template #cell(InvoicedPercent)="data">
              <div @click.stop="navigateToInvoices(data.item)">
                {{
                  $utils.numberWithMaxFrictionsDE(
                    (parseFloat(data.item.Invoiced) /
                      (parseFloat(data.item.Amount) + parseFloat(data.item.AmountAddendumOrdered))) *
                      100,
                    1
                  )
                }}
              </div>
            </template>
            <template #cell(AmountSaldo)="data">
              <div @click.stop="$router.push('/orders/' + data.item.DbId + '/addendums')">
                {{ $utils.formatCurrency(data.item.AmountSaldo, SettingsStore) }}
              </div>
            </template>
            <template #cell(NachtragsBudget)="data">
              <div @click.stop="$router.push('/orders/' + data.item.DbId + '/addendums')">
                {{ $utils.formatCurrency(data.item.BudgetSaldo - data.item.AmountSaldo, SettingsStore) }}
              </div>
            </template>

            <template #cell(AmountAddendumChecking)="data">
              <div @click.stop="$router.push('/orders/' + data.item.DbId + '/addendums')">
                {{ $utils.formatCurrency(data.item.AmountAddendumChecking, SettingsStore) }}
              </div>
            </template>
            <template #cell(ObligoAmount)="data">
              <div @click.stop="navigateToInvoices(data.item)">
                {{ $utils.formatCurrency(data.item.ObligoAmount, SettingsStore) }}
              </div>
            </template>
            <template #cell(HasFinalInvoice)="data">
              <!-- schlussgerechnet UND geprüft -->
              <b-badge
                pill
                class="sr-kennzeichen"
                v-if="data.item.HasFinalInvoice > 0 && data.item.FinalAmountStatus == 2"
                variant="info"
                style="font-size: 100%"
                title="schlussgerechnet, geprüft"
              >
                <b-icon class="p-0" icon="check-2" />
              </b-badge>
              <!-- schlussgerechnet, aber nicht geprüft -->
              <b-badge
                pill
                class="sr-kennzeichen light"
                v-if="data.item.HasFinalInvoice > 0 && data.item.FinalAmountStatus != 2"
                variant="info"
                style="font-size: 100%"
                title="schlussgerechnet, ungeprüft"
              >
                <b-icon class="p-0" icon="check-2" />
              </b-badge>
            </template>
            <template #cell(AbnahmeDatum)="data">
              <div>{{ data.value }}</div>
            </template>
          </b-table>
        </b-overlay>
        <TableFooter
          :displayed-count="displayedItemsCount"
          :selected-count="selectedRows.length"
          :loading="isLoading"
        />
      </div>
    </div>
    <TableColumnSelector
      :fields="fields"
      id="orders"
      @ok="AppState.updateVisibleFields('orders', fields)"
    ></TableColumnSelector>
  </div>
</template>

<script>
import AppState from '@/store/App.js';
import OrdersService from '@/service/OrdersService.js';
import SettingsStore from '@/store/Settings';
import BudgetService from '@/service/BudgetService';
import TableColumnSelector from '@/components/TableColumnSelector';
import TableFooter from '@/components/TableFooter';
import { copyTableToClipboard } from '@/utils/tableExport';

export default {
  components: {
    TableColumnSelector,
    TableFooter
  },
  data() {
    return {
      AppState,
      SettingsStore,
      isLoading: true,
      isHeaderLoading: true,
      tableHeight: '500px',
      //emptyBoxWidth: 0,
      checkedAllTableEntries: false,
      filterToggle: false,
      selectedRows: [],
      displayedItemsCount: 0,
      items: [],
      budgetKKE: 0.0,
      budgetPAT: 0.0,
      budgetVEBudget: 0.0,
      budgetVESkipped: 0.0,
      budgetVEBudgetOpen: 0.0,
      budgetVEBudgetInvoiced: 0.0,
      budgetVEBudgetAusgeschrieben: 0.0,
      budgetVEBudgetAusgewertet: 0.0,
      budgetVEBudgetInArbeit: 0.0,
      budgetOrderAmountSaldo: 0.0,
      budgetOrderAmountInvoiced: 0.0,
      budgetOrderAmountInArbeit: 0.0,
      budgetOrderAmountLeistungInvoiced: 0.0,
      budgetOrderAmountLeistungInvoicedFinal: 0.0,
      budgetOrderAmountLeistungInvoicedPartly: 0.0,
      budgetOrderMainOrders: 0.0,
      budgetOrderOrderedAddendums: 0.0,
      budgetOrderCheckingAddendums: 0.0,
      budgetOrderCheckingAddendumsFinal: 0.0,
      fields: [
        {
          hideable: false,
          visible: true,
          key: 'checked',
          label: '',
          tdClass: 'sticky-col first-col text-center align-middle',
          thClass: 'sticky-col first-col text-center align-top noselect no-resize',
          thStyle: 'z-index:10;width: 40px;'
        },
        {
          hideable: false,
          visible: true,
          key: 'Ordinal',
          label: 'VE',
          thClass: 'align-top sticky-col second-col',
          tdClass: 'sticky-col second-col',
          sortable: true,
          thStyle: 'z-index:10;width: 140px;'
        },
        {
          visible: true,
          hideable: false,
          key: 'DisplayName',
          label: 'Bezeichnung',
          thClass: 'align-top sticky-col third-col',
          tdClass: 'sticky-col third-col',
          sortable: true,
          thStyle: 'z-index:10;width: 300px;'
        },
        {
          hideable: SettingsStore.settings['KGR_Selections_Active'] == 'true',
          visible: SettingsStore.settings['KGR_Selections_Active'] == 'true',
          key: 'KGR',
          label: 'Kostengruppe',
          sortable: true,
          thClass: 'align-top',
          thStyle: 'width: 200px;'
        },
        {
          hideable: true,
          visible: true,
          key: 'Responsible',
          label: 'Verantwortlich',
          sortable: true,
          thClass: 'align-top',
          thStyle: 'width: 120px;'
        },
        {
          hideable: true,
          visible: true,
          key: 'FA1',
          label: SettingsStore.settings['FA1'],
          sortable: true,
          thClass: 'align-top',
          thStyle: 'width: 100px;'
        },
        {
          hideable: true,
          visible: true,
          key: 'FA2',
          label: SettingsStore.settings['FA2'],
          sortable: true,
          thClass: 'align-top',
          thStyle: 'width: 90px;'
        },
        {
          hideable: true,
          visible: true,
          key: 'ProcessStatus',
          label: 'Status',
          sortable: true,
          thClass: 'align-top',
          thStyle: 'width: 120px;'
        },
        {
          hideable: true,
          visible: true,
          key: 'SupplierText',
          label: 'Auftrag\u00adnehmer',
          tdClass: 'bg-white',
          sortable: true,
          thClass: 'align-top',
          thStyle: 'width: 160px; min-width: 100px;'
        },
        {
          hideable: true,
          visible: true,
          key: 'OrderStatus',
          label: 'Auftragsstatus / Datum',
          tdClass: 'bg-white',
          sortable: true,
          thClass: 'align-top',
          thStyle: 'width: 170px; min-width: 100px;',
          formatter: (value, key, item) => {
            if (item.veSection && item.veSection == 'A') {
              return (
                (item.VergabeEmpfehlung && item.VergabeEmpfehlung != '' ? item.VergabeEmpfehlung : 'Empfehlung') +
                ' / ' +
                this.$utils.SQLDateToGermanDate(item.VergabeEmpfehlungDatum)
              );
            } else if (item.veSection && item.veSection == 'B') {
              return (
                (item.OrderNumber && item.OrderNumber != '' ? item.OrderNumber : 'Bestellung') +
                ' / ' +
                this.$utils.SQLDateToGermanDate(item.OrderDate)
              );
            } else return '';
          },
          sortByFormatted: true
        },
        {
          hideable: true,
          visible: true,
          key: 'BudgetSaldo',
          label: 'Budget',
          formatter: x => this.$utils.formatCurrency(x, SettingsStore),
          sortable: true,
          thClass: 'align-top text-right',
          tdClass: 'text-right bg-primary-light-semi',
          thStyle: 'width: 150px; '
        },
        {
          hideable: true,
          visible: true,
          key: 'Amount',
          label: 'Haupt\u00adauftrag',
          formatter: x => this.$utils.formatCurrency(x, SettingsStore),
          tdClass: 'text-right bg-primary-light-2',
          sortable: true,
          thClass: 'align-top text-right',
          thStyle: 'width: 150px;'
        },
        {
          hideable: true,
          visible: true,
          key: 'AmountAddendumOrdered',
          label: 'Nachträge',
          formatter: x => this.$utils.formatCurrency(x, SettingsStore),
          tdClass: 'bg-primary-light-2 text-right',
          sortable: true,
          thClass: 'align-top text-right',
          thStyle: 'width: 150px;'
        },
        {
          hideable: true,
          visible: true,
          key: 'AmountSaldo',
          label: 'Gesamt\u00adauftrag',
          tdClass: 'bg-primary-light-2 text-right',
          sortable: true,
          thClass: 'align-top text-right',
          thStyle: 'width: 150px;',
          formatter: x => this.$utils.formatCurrency(x, SettingsStore)
        },
        {
          hideable: true,
          visible: true,
          key: 'NachtragsBudget',
          label: 'Nachtrags­budget',
          tdClass: 'bg-primary-light-2 text-right',
          sortable: true,
          thClass: 'align-top text-right',
          thStyle: 'width: 150px;',
          formatter: (k, v, item) => {
            return this.$utils.formatCurrency(item.BudgetSaldo - item.AmountSaldo, SettingsStore);
          }
        },
        {
          hideable: true,
          visible: true,
          key: 'AmountAddendumChecking',
          label: 'Offene Nachträge',
          tdClass: 'bg-primary-light-2 text-right',
          sortable: true,
          thClass: 'align-top text-right',
          thStyle: 'width: 150px;',
          formatter: x => this.$utils.formatCurrency(x, SettingsStore)
        },
        {
          hideable: true,
          visible: true,
          key: 'Invoiced',
          label: 'Abgerechnet',
          formatter: x => this.$utils.formatCurrency(x, SettingsStore),
          sortable: true,
          thClass: 'align-top text-right',
          tdClass: 'bg-primary-light-3 text-right',
          thStyle: 'width: 150px;'
        },
        {
          hideable: true,
          visible: true,
          key: 'InvoicedPercent',
          label: '%',
          sortable: true,
          thClass: 'align-top text-right',
          tdClass: 'bg-primary-light-3 text-right',
          thStyle: 'width: 60px;'
        },
        {
          hideable: true,
          visible: true,
          key: 'ObligoAmount',
          label: 'Obligo',
          formatter: x => this.$utils.formatCurrency(x, SettingsStore),
          sortable: true,
          thClass: 'align-top text-right',
          tdClass: 'bg-primary-light-3  text-right',
          thStyle: 'width: 150px;'
        },
        {
          hideable: true,
          visible: true,
          key: 'HasFinalInvoice',
          label: 'SR',
          formatter: x => (Number(x) > 0 ? 'Ja' : 'Nein'),
          sortable: true,
          thClass: 'align-top',
          thStyle: 'width: 70px; min-width: 50px;',
          tdClass: 'text-center'
        },
        {
          hideable: true,
          visible: true,
          key: 'AbnahmeDatum',
          label: 'Abnahme',
          formatter: x => this.$utils.SQLDateToGermanDate(x),
          sortable: true,
          thClass: 'align-top',
          thStyle: 'width: 110px; min-width: 50px;'
        },
        {
          hideable: false,
          visible: true,
          key: 'actions',
          label: 'Aktionen',
          tdClass: 'sticky-col last-col text-center align-middle',
          thClass: 'sticky-col last-col align-top noselect',
          thStyle: 'z-index: 10;'
        }
      ],
      budgetSaldoTotal: 0,
      amountTotal: 0,
      nachtragSummeTotal: 0,
      totalAmountTotal: 0,
      nachtragsBudgetTotal: 0,
      invoicedTotal: 0,
      obligoTotal: 0,
      checkingTotal: 0,
      searchText: ''
    };
  },
  methods: {
    async copyToExcel() {
      // Export the currently displayed rows in table order (search filter and sorting applied).
      const table = this.$refs.theTable;
      const items = (table && (table.computedItems || table.sortedItems)) || this.items;
      try {
        await copyTableToClipboard(this.visibleFields, items);
        this.$showSuccess(this, 'Excel-Export', items.length + ' Zeile(n) in die Zwischenablage kopiert.');
      } catch (error) {
        this.$showError(this, error.toString());
      }
    },
    recalcSums(filteredItems) {
      this.budgetSaldoTotal = 0;
      this.amountTotal = 0;
      this.nachtragSummeTotal = 0;
      this.totalAmountTotal = 0;
      this.nachtragsBudgetTotal = 0;
      this.invoicedTotal = 0;
      this.checkingTotal = 0;
      this.obligoTotal = 0;
      this.displayedItemsCount = filteredItems.length;
      filteredItems.map(x => {
        this.budgetSaldoTotal += parseFloat(x.BudgetSaldo);
        this.amountTotal += parseFloat(x.Amount);
        this.nachtragSummeTotal += parseFloat(x.AmountAddendumOrdered);
        this.totalAmountTotal += parseFloat(x.AmountSaldo);
        this.nachtragsBudgetTotal += parseFloat(x.BudgetSaldo) - parseFloat(x.AmountSaldo);
        this.invoicedTotal += parseFloat(x.Invoiced);
        this.checkingTotal += x.AmountAddendumChecking ? parseFloat(x.AmountAddendumChecking) : 0;
        if (x.HasFinalInvoice != 0 && x.FinalAmountChecked != 0) {
          x.ObligoAmount = 0;
        } else {
          x.ObligoAmount = parseFloat(
            (parseFloat(x.Amount) + parseFloat(x.AmountAddendumOrdered) - parseFloat(x.Invoiced)).toFixed(2)
          );
          this.obligoTotal += x.ObligoAmount;
        }

        //x.checked = false;
        return x;
      });
    },
    onFilterChange(visibleItems) {
      console.log('onFilterChange');
      // muss auf rowselected umgestellt werden, da sonst bei Nutzung der Suchfunktion
      // die Selektion verloren geht

      // remove check from all non-visible rows

      this.displayedItemsCount = visibleItems.length;

      this.selectedRows.forEach(item => {
        if (visibleItems.findIndex(x => x.DbId == item.DbId) == -1) {
          console.log('item not found in visible items, unselecting:', item);
          // item not found, uncheck it
          item.checked = false;

          // also remove from selected rows
          const index = this.selectedRows.findIndex(x => x.DbId == item.DbId);
          if (index != -1) {
            // remove from selected rows
            this.selectedRows.splice(index, 1);
          }
        }
      });
      // are all visible rows checked, then also check the "check all" checkbox

      let allChecked = true;

      visibleItems.forEach(item => {
        if (item.checked != true) allChecked = false;
      });
      this.checkedAllTableEntries = visibleItems.length > 0 && allChecked;
      this.recalcSums(visibleItems);
    },
    onClickColumnVisible() {
      AppState.updateVisibleFields('orders', this.fields);
    },
    onWindowResize() {
      setTimeout(() => {
        const totalHeight = window.innerHeight;
        const budgetSummarySection = this.$refs.budgetSummarySection;
        //const headerHeight = this.$refs.pageHeader.offsetHeight;
        const tableHeight = totalHeight - budgetSummarySection.offsetHeight - 130;
        this.tableHeight = tableHeight + 'px';
        //this.emptyBoxWidth = this.$refs.tableWrapper ? this.$refs.tableWrapper.clientWidth : 0;
      }, 300);
    },
    navigateTo(item, nachtrag = false) {
      if (item.editMode) return; // cancel navigation, if item is in edit mode
      this.$router.push('/orders/' + item.DbId + (nachtrag ? '/addendums' : '/view'));
    },
    navigateToVEBudgeting(item) {
      if (item.editMode) return; // cancel navigation, if item is in edit mode
      this.$router.push('/allocationunits/' + item.VeDbId);
    },
    navigateToInvoices(item) {
      if (item.editMode) return; // cancel navigation, if item is in edit mode
      this.$router.push('/invoices/' + item.DbId);
    },
    deleteRecord(item) {
      var questionText = '';

      OrdersService.maybeDeleted(item.DbId)
        .then(() => {
          if (item.ProcessStatus == 'empfohlen') {
            questionText = `Möchten Sie die Vergabeempfehlung wirklich zurückziehen?`;
          } else if (item.ProcessStatus == 'beauftragt') {
            questionText = `Möchten Sie den Auftrag wirklich löschen?<br><br>Bitte klären Sie ggf. die Konsequenzen einer Vertragskündigung.`;
          }

          const messageVNode = this.$createElement('div', { domProps: { innerHTML: questionText } });
          this.$bvModal
            .msgBoxConfirm([messageVNode], {
              title: 'Auftrag löschen',
              okTitle: 'Bestätigen',
              cancelTitle: 'Abbrechen',
              okVariant: 'danger',

              centered: true
            })
            .then(confirmed => {
              if (confirmed) {
                this.isLoading = true;
                OrdersService.delete(item)
                  .then(() => {
                    this.$utils.deleteItemFromArray(this.items, x => x.DbId == item.DbId);
                    this.$showSuccess(this, 'Auftrag löschen', 'Auftrag erfolgreich gelöscht');
                  })
                  .catch(err => {
                    if (err.response) this.$showError(this, err.response.data);
                    else this.$showError(this, err.toString());
                  })
                  .finally(() => (this.isLoading = false));
              }
            });
        })
        .catch(err => {
          console.log(err);
          questionText = 'Der Auftrag kann aus folgendem Grund nicht gelöscht werden:<br><br>';
          if (err.hasAddendums) questionText += '<li class="ml-3">Nachträge vorhanden</li>';
          if (err.hasInvoices) questionText += '<li class="ml-3">Belege vorhanden</li>';
          questionText += '<br>Bitte löschen Sie zunächst diese Objekte, bevor der Auftrag gelöscht werden kann.';

          const messageVNode = this.$createElement('div', { domProps: { innerHTML: questionText } });

          this.$bvModal.msgBoxOk([messageVNode], {
            title: 'Auftrag löschen',
            okTitle: 'OK',
            okVariant: 'danger',

            centered: true
          });
        });
    },
    onCheckAllTableEntries() {
      console.log('onCheckAllTableEntries');
      this.checkedSomeTableEntries = this.checkedAllTableEntries;
      // select or unselect all items
      this.items.map(x => {
        return (x.checked = this.checkedAllTableEntries);
      });
      this.selectedRows = this.items.filter(x => x.checked == true);
    },
    onCheckedItemChange(item) {
      this.checkedSomeTableEntries = false;
      this.selectedRows = this.items.filter(x => x.checked == true);
      for (let i = 0; i < this.items.length; i++) {
        if (this.items[i].checked) {
          this.checkedSomeTableEntries = true;
          break;
        }
      }
      if (!item.checked) {
        return (this.checkedAllTableEntries = false);
      }

      for (let i = 0; i < this.items.length; i++) {
        if (!this.items[i].checked) {
          return (this.checkedAllTableEntries = false);
        }
      }

      this.checkedAllTableEntries = true;
    },
    filterRecords() {
      if (!this.filterToggle) {
        this.filterToggle = true;
        this.itemsBackup = [...this.items];
        this.items = this.selectedRows;
        this.$nextTick(() => {
          this.$refs.theTable.selectAllRows();
        });
      } else {
        this.filterToggle = false;
        this.items = this.itemsBackup;
        this.selectedRows.forEach(row => {
          this.$nextTick(() => this.$refs.theTable.selectRow(this.items.findIndex(item => row.DbId == item.DbId)));
        });
      }
      this.recalcSums(this.items);
    }
  },
  computed: {
    visibleFields() {
      return this.fields.filter(field => field.visible == true || field.visible == 'true');
    },
    isTabOpen() {
      return localStorage.getItem('collapse-orders') == 'true' ? true : false;
    }
  },
  created() {
    this.$eventBus.$on('projectSwitched', () => {
      this.searchText = '';
    });
  },
  activated() {
    AppState.loadVisibleFields('orders', this.fields);
    window.addEventListener('resize', this.onWindowResize); // listen for windows size changes, to re-compute height of controls

    this.fields.find(x => x.key == 'KGR').hideable = SettingsStore.settings['KGR_Selections_Active'] == 'true';
    this.fields.find(x => x.key == 'Responsible').hideable =
      SettingsStore.settings['Responsible_Selections_Active'] == 'true';
    this.fields.find(x => x.key == 'FA1').hideable = SettingsStore.settings['FA1_Active'] == 'true';
    this.fields.find(x => x.key == 'FA2').hideable = SettingsStore.settings['FA2_Active'] == 'true';

    this.fields.find(x => x.key == 'KGR').visible =
      this.fields.find(x => x.key == 'KGR').visible && SettingsStore.settings['KGR_Selections_Active'] == 'true';
    this.fields.find(x => x.key == 'Responsible').visible =
      this.fields.find(x => x.key == 'Responsible').visible &&
      SettingsStore.settings['Responsible_Selections_Active'] == 'true';
    this.fields.find(x => x.key == 'FA1').visible =
      this.fields.find(x => x.key == 'FA1').visible && SettingsStore.settings['FA1_Active'] == 'true';
    this.fields.find(x => x.key == 'FA2').visible =
      this.fields.find(x => x.key == 'FA2').visible && SettingsStore.settings['FA2_Active'] == 'true';

    this.fields.find(x => x.key == 'FA1').label = SettingsStore.settings['FA1'];
    this.fields.find(x => x.key == 'FA2').label = SettingsStore.settings['FA2'];

    this.onWindowResize();

    this.isLoading = true;
    this.isHeaderLoading = true;
    AppState.isLoading = false;
    BudgetService.getSummary()
      .then(summary => {
        this.budgetKKE = summary.KkeBudgetSummary;
        this.budgetPAT = summary.PatBudgetSummary;

        this.budgetVESkipped = summary.VEBudgetSkipped;
        this.budgetVEBudgetOpen = summary.VEBudgetOpen;
        this.budgetVEBudgetInvoiced = summary.VEBudgetInvoiced;
        this.budgetVEBudgetAusgeschrieben = summary.VEBudgetAusgeschrieben;
        this.budgetVEBudgetAusgewertet = summary.VEBudgetAusgewertet;
        this.budgetVEBudgetInArbeit = summary.VEBudgetInArbeit;
        this.budgetOrderAmountSaldo = summary.OrderAmountSaldo;
        this.budgetOrderAmountInvoiced = summary.OrderAmountInvoiced;
        this.budgetOrderAmountInArbeit = summary.OrderAmountInArbeit;
        this.budgetOrderAmountLeistungInvoiced = summary.OrderAmountLeistungInvoiced;
        this.budgetOrderAmountLeistungInvoicedFinal = summary.OrderAmountLeistungInvoicedFinal;
        this.budgetOrderAmountLeistungInvoicedPartly = summary.OrderAmountLeistungInvoicedPartly;
        this.budgetVEBudget =
          this.budgetVESkipped +
          this.budgetVEBudgetOpen +
          this.budgetVEBudgetAusgeschrieben +
          this.budgetVEBudgetAusgewertet +
          this.budgetVEBudgetInArbeit +
          this.budgetVEBudgetInvoiced;
        this.budgetOrderMainOrders = summary.OrderMainOrders;
        this.budgetOrderOrderedAddendums = summary.OrderOrderedAddendums;
        this.budgetOrderCheckingAddendums = summary.OrderCheckingAddendums;
        this.budgetOrderCheckingAddendumsFinal = summary.OrderCheckingAddendumsFinal;
      })
      .catch(err => console.log(err.toString()))
      .finally(() => {
        this.isHeaderLoading = false;
      });
    OrdersService.get()
      .then(items => {
        this.budgetSaldoTotal = 0;
        this.amountTotal = 0;
        this.nachtragSummeTotal = 0;
        this.totalAmountTotal = 0;
        this.nachtragsBudgetTotal = 0;
        this.invoicedTotal = 0;
        this.checkingTotal = 0;
        this.obligoTotal = 0;
        this.items = items.map(x => {
          this.budgetSaldoTotal += parseFloat(x.BudgetSaldo);
          this.amountTotal += parseFloat(x.Amount);
          this.nachtragSummeTotal += parseFloat(x.AmountAddendumOrdered);
          this.totalAmountTotal += parseFloat(x.AmountSaldo);
          this.nachtragsBudgetTotal += parseFloat(x.BudgetSaldo) - parseFloat(x.AmountSaldo);
          this.invoicedTotal += parseFloat(x.Invoiced);
          this.checkingTotal += x.AmountAddendumChecking ? parseFloat(x.AmountAddendumChecking) : 0;
          if (x.HasFinalInvoice != 0 && x.FinalAmountChecked != 0) {
            x.ObligoAmount = 0;
          } else {
            x.ObligoAmount = parseFloat(x.Amount) + parseFloat(x.AmountAddendumOrdered) - parseFloat(x.Invoiced);
            this.obligoTotal += x.ObligoAmount;
          }

          x.checked = false;
          return x;
        });
        this.selectedRows.forEach(row => {
          this.$nextTick(() => this.$refs.theTable.selectRow(this.items.findIndex(item => row.DbId == item.DbId)));
        });

        if (this.filterToggle) {
          this.itemsBackup = [...this.items];
          this.items = this.selectedRows;
          this.$nextTick(() => {
            this.$refs.theTable.selectAllRows();
          });
        }
        this.isLoading = false;
      })
      .finally(() => {
        if (AppState.scrollPosition[this.$route.path])
          this.$refs.theTable.$el.scrollTo({
            top: AppState.scrollPosition[this.$route.path][0],
            left: AppState.scrollPosition[this.$route.path][1]
          });
        this.displayedItemsCount = this.items.length;
      });
  },

  deactivated() {
    window.removeEventListener('resize', this.onWindowResize);
  },
  beforeRouteLeave(to, from, next) {
    AppState.scrollPosition[from.path] = [this.$refs.theTable.$el.scrollTop, this.$refs.theTable.$el.scrollLeft];
    next();
  }
};
</script>

<style>
.page-header {
  border-bottom: 1px solid #dee2e6;
}

.table-wrapper {
  border: 1px solid #dee2e6;
}

.first-col {
  left: 0;
}

.second-col {
  left: 40px;
}

.third-col {
  left: 180px;
  text-overflow: ellipsis;
  overflow: hidden;
}

.last-col {
  width: 100px;
  min-width: 100px;
  max-width: 100px;
  right: 0;
}

.table-wrapper tbody tr td.sticky-col {
  background-color: var(--bauvisor-primary-light-semi) !important;
}

.table-striped tbody tr:nth-of-type(odd) td.sticky-col {
  background-color: #efefef !important;
}

.table-striped tbody tr:nth-of-type(even) td.sticky-col {
  background-color: #f5f5f5 !important;
}

.table-wrapper td:not(:last-child) {
  border-right: 1px solid #dee2e6;
}

.table-wrapper td:last-child {
  border-left: 1px solid #ced2d6;
}

.table-wrapper tr:last-child td {
  border-bottom: 1px solid #ced2d6;
}

.table-wrapper td:nth-last-child(2) {
  border-left: none;
  border-right: none;
}

.table.b-table > tbody > .table-active,
.table.b-table > tbody > .table-active > th,
.table.b-table > tbody > .table-active > td {
  background-color: #f5f5f5;
}

#column-selector-orders-modal > .modal-dialog {
  position: absolute;
  top: 100px;
  right: 13px;
}

.sr-kennzeichen {
  display: inline;
  vertical-align: middle;
  border-radius: 4px;
  padding-left: 3px;
  padding-right: 3px;
  padding-top: 0;
  padding-bottom: 0;
}
</style>
